import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import delivery3 from "../assets/delivery3.png";
import "../styles/HealthPackages.css";

/* ─────────────────────────────────────────────
   WhatsApp number — same as Navbar contact number
   (73587 98182) prefixed with India country code
───────────────────────────────────────────── */
const WHATSAPP_NUMBER = "917358798182";

/* Route + section id that "View All Tests" should land on */
const VIEW_ALL_ROUTE = "/service";
const VIEW_ALL_TARGET_ID = "diagnosticservices";

function scrollToId(id, retries = 10) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (retries > 0) {
    setTimeout(() => scrollToId(id, retries - 1), 80);
  }
}

function openWhatsApp(message) {
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

const packages = [
  {
    id: 1,
    icon: "fa-solid fa-heart-pulse",
    iconColor: "#2563eb",
    title: "Lipid Profile Basic",
    originalPrice: 400,
    price: 280,
    tests: [
      "Total Cholesterol",
      "Triglycerides",
      "HDL Cholesterol",
      "LDL Cholesterol",
      "VLDL Cholesterol",
      "CHOL/HDL Ratio",
      "LDL/HDL Ratio",
    ],
  },
  {
    id: 2,
    icon: "fa-solid fa-notes-medical",
    iconColor: "#0891b2",
    title: "Thyroid Detection Package",
    originalPrice: 350,
    price: 245,
    tests: ["T3", "T4", "TSH"],
  },
  {
    id: 3,
    icon: "fa-solid fa-droplet",
    iconColor: "#dc2626",
    title: "Joint & Inflammation Panel",
    originalPrice: 750,
    price: 525,
    tests: ["Complete Blood Cell Count", "RA Factor", "CRP", "Uric Acid"],
  },
  {
    id: 4,
    icon: "fa-solid fa-flask",
    iconColor: "#16a34a",
    title: "Liver & Kidney Panel",
    originalPrice: 900,
    price: 630,
    tests: ["LFT (Liver Function Test)", "RFT (Renal Function Test)"],
  },
  {
    id: 5,
    icon: "fa-solid fa-sun",
    iconColor: "#f59e0b",
    title: "Vitamin-D & Bone Health",
    originalPrice: 1350,
    price: 945,
    tests: ["Vitamin-D", "Calcium", "Phosphorus", "Alkaline Phosphate"],
  },
  {
    id: 6,
    icon: "fa-solid fa-list-check",
    iconColor: "#9333ea",
    title: "Comprehensive Metabolic Package",
    originalPrice: 1250,
    price: 875,
    tests: [
      "Complete Blood Cell Count",
      "Blood Sugar (F)",
      "Blood Sugar (PP)",
      "Uric Acid",
      "Calcium",
      "Phosphorus",
      "Lipid Profile",
      "Creatinine",
    ],
  },
  {
    id: 7,
    icon: "fa-solid fa-venus",
    iconColor: "#db2777",
    title: "Women's Hormone & Wellness Package",
    originalPrice: 2050,
    price: 1435,
    tests: [
      "Blood Sugar (F)",
      "Blood Sugar (PP)",
      "LH",
      "FSH",
      "Prolactin",
      "Thyroid Function Test",
      "Complete Blood Cell Count",
      "Urine Routine Examination",
    ],
  },
  {
    id: 8,
    icon: "fa-solid fa-user-group",
    iconColor: "#ea580c",
    title: "Family Health Package",
    originalPrice: 1800,
    price: 1260,
    tests: [
      "Complete Blood Cell Count",
      "Blood Sugar (F)",
      "Blood Sugar (PP)",
      "Lipid Profile",
      "LFT",
      "RFT",
      "Urine Routine Examination",
    ],
  },
  {
    id: 9,
    icon: "fa-solid fa-crown",
    iconColor: "#b45309",
    title: "Complete Body Checkup Package",
    badge: "Most Popular",
    originalPrice: 2150,
    price: 1505,
    tests: [
      "Complete Blood Cell Count",
      "Blood Sugar (F)",
      "Blood Sugar (PP)",
      "Lipid Profile",
      "LFT",
      "RFT",
      "Thyroid Function Test",
      "Urine Routine Examination",
    ],
  },
];

const features = [
  { icon: "fa-solid fa-user-check", label: "Trained", sub: "Phlebotomists" },
  { icon: "fa-solid fa-shield-halved", label: "Safe & Hygienic", sub: "Collection" },
  { icon: "fa-solid fa-clock", label: "On-time", sub: "Report Delivery" },
];

function getDiscountPercent(pkg) {
  return Math.round(
    ((pkg.originalPrice - pkg.price) / pkg.originalPrice) * 100
  );
}

export default function HealthPackages() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollTicking = useRef(false);

  /* ── entrance animation ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* ── carousel: track active card while scrolling ── */
  const handleTrackScroll = useCallback(() => {
    if (scrollTicking.current) return;
    scrollTicking.current = true;

    requestAnimationFrame(() => {
      const track = trackRef.current;
      if (track) {
        const cards = Array.from(track.children);
        const scrollLeft = track.scrollLeft;
        let closest = 0;
        let minDist = Infinity;

        cards.forEach((card, i) => {
          const dist = Math.abs(card.offsetLeft - scrollLeft);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });

        setActiveIndex(closest);
      }
      scrollTicking.current = false;
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener("scroll", handleTrackScroll, { passive: true });
    return () => track.removeEventListener("scroll", handleTrackScroll);
  }, [handleTrackScroll]);

  /* ── carousel controls ── */
  function scrollByCards(direction) {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.children[0];
    const gap = 24;
    const cardWidth = firstCard
      ? firstCard.getBoundingClientRect().width + gap
      : 300;
    track.scrollBy({ left: direction * cardWidth, behavior: "smooth" });
  }

  function scrollToIndex(index) {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[index];
    if (card) {
      track.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
    }
  }

  /* ── WhatsApp booking ── */
  function handleBookNow(pkg) {
    const message = `Hi, I'm interested in booking the *${pkg.title}* (₹${pkg.price}, originally ₹${pkg.originalPrice}). Please share the available slots.`;
    openWhatsApp(message);
  }

  /* ── WhatsApp home sample collection scheduling ── */
  function handleScheduleCollection() {
    openWhatsApp(
      "Hi, I'd like to schedule a home sample collection. Please share the available slots."
    );
  }

  /* ── View All Tests → Diagnostic Services section ── */
  function handleViewAllPackages() {
    if (location.pathname === VIEW_ALL_ROUTE) {
      scrollToId(VIEW_ALL_TARGET_ID);
    } else {
      navigate(VIEW_ALL_ROUTE);
      setTimeout(() => scrollToId(VIEW_ALL_TARGET_ID), 120);
    }
  }

  return (
    <section className="hp-section" ref={sectionRef} id="packages">

      {/* ── HEADER — subtitle + title + description, centered on top ── */}
      <div className={`hp-header ${visible ? "hp-header-visible" : ""}`}>
        <span className="hp-subtitle hp-animate-up hp-delay-0">
          HEALTH PACKAGES
        </span>
        <h2 className="hp-title hp-animate-up hp-delay-1">
          Stay Healthy, Stay Ahead
        </h2>
        <p className="hp-description hp-animate-up hp-delay-2">
          Choose from our curated health packages designed for every stage of life.
        </p>
      </div>

      {/* ── CAROUSEL ── */}
      <div
        className={`hp-carousel-container hp-anim-up ${visible ? "hp-anim-in" : ""}`}
        style={{ transitionDelay: "0.3s" }}
      >
        <button
          type="button"
          className="hp-carousel-arrow hp-carousel-arrow--prev"
          onClick={() => scrollByCards(-1)}
          aria-label="Previous packages"
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>

        <div className="hp-carousel-track" ref={trackRef}>
          {packages.map((pkg) => {
            const discount = getDiscountPercent(pkg);
            return (
              <div key={pkg.id} className="hp-carousel-item">
                <div
                  className="hp-card h-100 d-flex flex-column"
                  style={{ "--card-accent": pkg.iconColor }}
                >
                  {pkg.badge && (
                    <span className="hp-badge-ribbon">{pkg.badge}</span>
                  )}

                  <div className="hp-card-header d-flex align-items-center gap-3 mb-3">
                    <span
                      className="hp-icon-wrap"
                      style={{ color: pkg.iconColor, borderColor: pkg.iconColor }}
                    >
                      <i className={pkg.icon}></i>
                    </span>
                    <span className="hp-card-title">{pkg.title}</span>
                  </div>

                  <div className="hp-price-wrap text-center mb-3">
                    <div className="hp-price-row d-flex align-items-center justify-content-center gap-2">
                      <span className="hp-price-original">
                        ₹{pkg.originalPrice}
                      </span>
                      <span className="hp-discount-badge">
                        {discount}% OFF
                      </span>
                    </div>
                    <div className="hp-price" style={{ color: pkg.iconColor }}>
                      ₹{pkg.price}
                    </div>
                    <div className="hp-price-divider"></div>
                  </div>

                  <ul className="hp-tests list-unstyled flex-grow-1 mb-4">
                    {pkg.tests.map((t) => (
                      <li key={t} className="d-flex align-items-center gap-2 mb-1">
                        <i className="fa-solid fa-circle-check" style={{ color: pkg.iconColor }}></i>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className="hp-book-btn w-100"
                    onClick={() => handleBookNow(pkg)}
                  >
                    <i className="fa-brands fa-whatsapp me-2"></i>
                    Book Now
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="hp-carousel-arrow hp-carousel-arrow--next"
          onClick={() => scrollByCards(1)}
          aria-label="Next packages"
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      {/* ── DOTS ── */}
      <div className="hp-carousel-dots">
        {packages.map((pkg, i) => (
          <button
            key={pkg.id}
            type="button"
            className={`hp-carousel-dot${activeIndex === i ? " hp-carousel-dot--active" : ""}`}
            onClick={() => scrollToIndex(i)}
            aria-label={`Go to ${pkg.title}`}
          ></button>
        ))}
      </div>

      {/* ── VIEW ALL ── */}
      <div
        className={`hp-view-all-wrapper hp-anim-up ${visible ? "hp-anim-in" : ""}`}
        style={{ transitionDelay: "0.5s" }}
      >
        <button className="hp-view-all-btn" onClick={handleViewAllPackages}>
          View All Tests <i className="fa-solid fa-arrow-right ms-2"></i>
        </button>
      </div>

      {/* ── DELIVERY BANNER ── */}
      <div className="container-fluid px-4">
        <div
          className={`hp-delivery-outer hp-anim-up ${visible ? "hp-anim-in" : ""}`}
          style={{ transitionDelay: "0.65s" }}
        >
          <div className="hp-delivery-row position-relative">

            <div className="hp-delivery-bg"></div>

            {/* DESKTOP (≥ 992px) */}
            <div className="hp-delivery-img-wrap d-none d-lg-block">
              <img src={delivery3} alt="Home Sample Collection" loading="lazy" className="hp-delivery-img" />
            </div>

            <div className="hp-delivery-content-desktop d-none d-lg-flex align-items-center justify-content-between w-100">
              <div className="hp-delivery-text">
                <h3 className="hp-delivery-title">Home Sample Collection</h3>
                <p className="hp-delivery-sub">We collect samples from the comfort of your home.</p>
              </div>
              <div className="hp-features d-flex gap-4">
                {features.map((f) => (
                  <div key={f.label} className="hp-feature-item d-flex align-items-center gap-2">
                    <span className="hp-feature-icon"><i className={f.icon}></i></span>
                    <div>
                      <div className="hp-feature-label">{f.label}</div>
                      <div className="hp-feature-sub">{f.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                className="hp-schedule-btn d-flex align-items-center gap-2"
                onClick={handleScheduleCollection}
              >
                <i className="fa-regular fa-calendar-check"></i>
                Schedule Collection
              </button>
            </div>

            {/* TABLET (576px – 991px) */}
            <div className="hp-delivery-content-tablet d-none d-sm-flex d-lg-none w-100 align-items-center">
              <div className="hp-tab-img-col">
                <img src={delivery3} alt="Home Sample Collection" loading="lazy" className="hp-tab-img" />
              </div>
              <div className="hp-tab-desc-col d-flex flex-column justify-content-center">
                <h3 className="hp-delivery-title">Home Sample Collection</h3>
                <p className="hp-delivery-sub">We collect samples from the comfort of your home.</p>
              </div>
              <div className="hp-tab-right-col d-flex flex-column justify-content-center gap-3">
                <div className="d-flex flex-column gap-2">
                  {features.map((f) => (
                    <div key={f.label} className="hp-feature-item d-flex align-items-center gap-2">
                      <span className="hp-feature-icon"><i className={f.icon}></i></span>
                      <div>
                        <div className="hp-feature-label">{f.label}</div>
                        <div className="hp-feature-sub">{f.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="hp-schedule-btn d-flex align-items-center justify-content-center gap-2"
                  onClick={handleScheduleCollection}
                >
                  <i className="fa-regular fa-calendar-check"></i>
                  Schedule Collection
                </button>
              </div>
            </div>

            {/* MOBILE (< 576px) */}
            <div className="hp-delivery-content-mobile d-flex d-sm-none flex-column w-100">
              <div className="hp-mob-row1 text-center">
                <h3 className="hp-delivery-title">Home Sample Collection</h3>
                <p className="hp-delivery-sub">We collect samples from the comfort of your home.</p>
              </div>
              <div className="hp-mob-row2 d-flex align-items-center">
                <div className="hp-mob-img-col">
                  <img src={delivery3} alt="Home Sample Collection" loading="lazy" className="hp-mob-img" />
                </div>
                <div className="hp-mob-features-col d-flex flex-column justify-content-center gap-2">
                  {features.map((f) => (
                    <div key={f.label} className="hp-feature-item d-flex align-items-center gap-2">
                      <span className="hp-feature-icon"><i className={f.icon}></i></span>
                      <div>
                        <div className="hp-feature-label">{f.label}</div>
                        <div className="hp-feature-sub">{f.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hp-mob-row3 d-flex justify-content-center">
                <button
                  className="hp-schedule-btn d-flex align-items-center gap-2"
                  onClick={handleScheduleCollection}
                >
                  <i className="fa-regular fa-calendar-check"></i>
                  Schedule Collection
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

    </section>
  );
}